import { useDisableCRUD } from "../../../../../../store/admin/companiesStore";
import type { UseFormReturn } from "react-hook-form";
import type { ThemeClasses, RoutePoint, RouteFormValues } from "../types";
import { LINEAR_BUFFER_DEFAULT } from "../types";
import Modal from "../../../../../../components/base/Modal";
import { APIProvider } from "@vis.gl/react-google-maps";
import { RouteForm } from "./RouteForm";
import { RouteMapPreview } from "./RouteMapPreview";

interface RouteModalProps {
  isOpen: boolean;
  isEdit: boolean;
  loadingSubmit: boolean;
  form: UseFormReturn<RouteFormValues>;
  themeClasses: ThemeClasses;
  isDarkMode: boolean;
  routePoints: RoutePoint[];
  routeDistance: number;
  estimatedTime: string;
  hasRealChanges: () => boolean;
  onCoordinateEdit: (index: number, field: "latitude" | "longitude", raw: string) => void;
  onAddressEdit: (index: number, rawAddress: string) => void;
  onAddPoint: () => void;
  onRemovePoint: (index: number) => void;
  onMapPointsChange: (newPoints: { lat: number; lng: number }[]) => void;
  onRouteCalculated: (distance: number) => void;
  onAxisCalculated: (path: { lat: number; lng: number }[]) => void;
  onCorridorBufferChange: (value: number) => void;
  onSubmit: (data: RouteFormValues) => void;
  onClose: () => void;
}

export function RouteModal({
  isOpen,
  isEdit,
  loadingSubmit,
  form,
  themeClasses,
  isDarkMode,
  routePoints,
  routeDistance,
  estimatedTime,
  hasRealChanges,
  onCoordinateEdit,
  onAddressEdit,
  onAddPoint,
  onRemovePoint,
  onMapPointsChange,
  onRouteCalculated,
  onAxisCalculated,
  onCorridorBufferChange,
  onSubmit,
  onClose,
}: RouteModalProps) {
  const disableCRUD = useDisableCRUD();

  if (!isOpen) return null;

  return (
    <Modal
      title={isEdit ? "Editar Ruta" : "Nueva Ruta"}
      onClose={onClose}
      themeClasses={themeClasses}
      width="w-full sm:w-[80vw]"
      disableCRUD={disableCRUD}
      content={
        <APIProvider
          apiKey={import.meta.env.VITE_GOOGLE_MAPS_API_KEY}
          libraries={["places"]}
        >
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-6 h-auto">
            {/* Left panel: form */}
            <RouteForm
              form={form}
              isEdit={isEdit}
              loadingSubmit={loadingSubmit}
              hasRealChanges={hasRealChanges}
              themeClasses={themeClasses}
              isDarkMode={isDarkMode}
              routePoints={routePoints}
              onCoordinateEdit={onCoordinateEdit}
              onAddressEdit={onAddressEdit}
              onAddPoint={onAddPoint}
              onRemovePoint={onRemovePoint}
              onCorridorBufferChange={onCorridorBufferChange}
              onSubmit={onSubmit}
              onCancel={onClose}
            />

            {/* Right panel: map */}
            <div className="lg:w-full h-[75vh] max-h-[900px] rounded-xl overflow-hidden">
              <RouteMapPreview
                themeClasses={themeClasses}
                isDarkMode={isDarkMode}
                isEdit={isEdit}
                routePoints={routePoints}
                routeDistance={routeDistance}
                estimatedTime={estimatedTime}
                firstPointRadius={form.watch("first_point_radius") || 0}
                lastPointRadius={form.watch("last_point_radius") || 0}
                corridorBuffer={
                  form.watch("principal_route_buffer") || LINEAR_BUFFER_DEFAULT
                }
                onPointsChange={onMapPointsChange}
                onRouteCalculated={onRouteCalculated}
                onAxisCalculated={onAxisCalculated}
                onFirstPointRadiusChange={(v) =>
                  form.setValue("first_point_radius", v, { shouldValidate: true })
                }
                onLastPointRadiusChange={(v) =>
                  form.setValue("last_point_radius", v, { shouldValidate: true })
                }
              />
            </div>
          </div>
        </APIProvider>
      }
    />
  );
}
